// Shared Zod-Schemas und Typen für API + Web.
// Wird ohne Build-Step direkt als TS konsumiert (via tsconfig paths / vite alias).

import { z } from 'zod';

export const SOURCE_FORMATS = ['EXCEL', 'CSV_SEMICOLON'] as const;
export type SourceFormat = (typeof SOURCE_FORMATS)[number];

export const RUN_STATES = [
  'QUEUED',
  'RUNNING',
  'DONE',
  'FAILED',
  'CANCELLED',
  'INTERRUPTED',
] as const;
export type RunState = (typeof RUN_STATES)[number];

export const RECORD_STATUSES = ['OK', 'SKIPPED', 'ERROR'] as const;
export type RecordStatus = (typeof RECORD_STATUSES)[number];

export const FORMAT_RULES = ['DATE_DE', 'BOOL_10', 'BOOL_10_INVERT'] as const;
export type FormatRule = (typeof FORMAT_RULES)[number];

// ── Auth ─────────────────────────────────────────────────────────────────────

export const LoginBodySchema = z.object({
  username: z.string().min(1, 'Benutzername erforderlich'),
  password: z.string().min(1, 'Passwort erforderlich'),
});
export type LoginBody = z.infer<typeof LoginBodySchema>;

export const CurrentUserSchema = z.object({
  id: z.string(),
  username: z.string(),
  role: z.string(),
});
export type CurrentUser = z.infer<typeof CurrentUserSchema>;

// ── Connection Profiles ───────────────────────────────────────────────────────

export const ConnectionProfileCreateSchema = z.object({
  name: z.string().min(1, 'Name erforderlich').max(100),
  host: z.string().min(1, 'Host erforderlich'),
  port: z.coerce.number().int().min(1).max(65535).default(1433),
  database: z.string().min(1, 'Datenbank erforderlich'),
  username: z.string().min(1, 'Benutzername erforderlich'),
  password: z.string().min(1, 'Passwort erforderlich'),
  encrypt: z.boolean().default(true),
  trustServerCert: z.boolean().default(false),
});
export type ConnectionProfileCreate = z.infer<typeof ConnectionProfileCreateSchema>;

export const ConnectionProfileUpdateSchema = ConnectionProfileCreateSchema.omit({
  password: true,
})
  .partial()
  .extend({
    password: z.string().min(1).optional(),
  });
export type ConnectionProfileUpdate = z.infer<typeof ConnectionProfileUpdateSchema>;

export const ConnectionProfileResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  host: z.string(),
  port: z.number(),
  database: z.string(),
  username: z.string(),
  encrypt: z.boolean(),
  trustServerCert: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ConnectionProfileResponse = z.infer<typeof ConnectionProfileResponseSchema>;

// ── Import Profiles ───────────────────────────────────────────────────────────

export const FUNCTION_KEYS = [
  'isIndex',
  'staticIfEmpty',
  'concat.newline',
  'concat.space',
  'lookup.unit',
  'lookup.productGroup',
  'text.toRtf',
  'text.fromRtf',
] as const;
export type FunctionKey = (typeof FUNCTION_KEYS)[number];

export const FUNCTION_LABELS: Record<FunctionKey, string> = {
  isIndex: 'Ist Index (Schlüsselfeld)',
  staticIfEmpty: 'Wenn leer, dann statischen Wert',
  'concat.newline': 'Daten anhängen (Enter davor)',
  'concat.space': 'Daten anhängen (Leerzeichen davor)',
  'lookup.unit': 'Mengeneinheits-ID über Bezeichnung',
  'lookup.productGroup': 'Warengruppen-Nr über Bezeichnung',
  'text.toRtf': 'Text in RTF wandeln',
  'text.fromRtf': 'RTF in Text wandeln',
};

export const MappingCreateSchema = z.object({
  position: z.coerce.number().int().min(0).default(0),
  targetField: z.string().min(1, 'Zielfeld erforderlich'),
  sourceField: z.string().optional(),
  staticValue: z.string().optional(),
  functionKey: z.enum(FUNCTION_KEYS).optional(),
  functionParamsJson: z.string().optional(),
  maxLength: z.coerce.number().int().positive().optional(),
  formatRule: z.enum(FORMAT_RULES).optional(),
  emptyTransfers: z.boolean().default(false),
  required: z.boolean().default(false),
  hint: z.string().optional(),
});
export type MappingCreate = z.infer<typeof MappingCreateSchema>;

export const MappingUpdateSchema = MappingCreateSchema.partial();
export type MappingUpdate = z.infer<typeof MappingUpdateSchema>;

export const MappingResponseSchema = z.object({
  id: z.string(),
  profileId: z.string(),
  position: z.number(),
  targetField: z.string(),
  sourceField: z.string().nullable(),
  staticValue: z.string().nullable(),
  functionKey: z.string().nullable(),
  functionParamsJson: z.string().nullable(),
  maxLength: z.number().nullable(),
  formatRule: z.string().nullable(),
  emptyTransfers: z.boolean(),
  required: z.boolean(),
  hint: z.string().nullable(),
});
export type MappingResponse = z.infer<typeof MappingResponseSchema>;

export const ImportProfileCreateSchema = z.object({
  name: z.string().min(1, 'Name erforderlich').max(200),
  bereich: z.string().min(1, 'Bereich erforderlich'),
  targetTable: z
    .string()
    .min(1, 'Zieltabelle erforderlich')
    .regex(/^[\w\s]+$/, 'Ungültige Zeichen im Tabellennamen'),
  connectionId: z.string().min(1, 'Verbindung erforderlich'),
  sourceFormat: z.enum(SOURCE_FORMATS),
  sheetName: z.string().optional(),
  hasHeader: z.boolean().default(true),
  onlyChanged: z.boolean().default(true),
  isActive: z.boolean().default(true),
});
export type ImportProfileCreate = z.infer<typeof ImportProfileCreateSchema>;

export const ImportProfileUpdateSchema = ImportProfileCreateSchema.partial();
export type ImportProfileUpdate = z.infer<typeof ImportProfileUpdateSchema>;

export const ImportProfileResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  bereich: z.string(),
  targetTable: z.string(),
  connectionId: z.string(),
  sourceFormat: z.enum(SOURCE_FORMATS),
  sheetName: z.string().nullable(),
  hasHeader: z.boolean(),
  onlyChanged: z.boolean(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  mappings: z.array(MappingResponseSchema).optional(),
});
export type ImportProfileResponse = z.infer<typeof ImportProfileResponseSchema>;

// ── Import Runs ───────────────────────────────────────────────────────────────

export const ImportRunResponseSchema = z.object({
  id: z.string(),
  profileId: z.string(),
  startedAt: z.string(),
  finishedAt: z.string().nullable(),
  state: z.enum(RUN_STATES),
  triggeredBy: z.string(),
  sourceFileName: z.string(),
  sourceFileSize: z.number(),
  sourceFileSha256: z.string(),
  rowsRead: z.number(),
  rowsOk: z.number(),
  rowsSkipped: z.number(),
  rowsError: z.number(),
  errorSummary: z.string().nullable(),
});
export type ImportRunResponse = z.infer<typeof ImportRunResponseSchema>;

export const RunRecordResponseSchema = z.object({
  id: z.string(),
  runId: z.string(),
  rowIndex: z.number(),
  recordKey: z.string().nullable(),
  status: z.enum(RECORD_STATUSES),
  message: z.string().nullable(),
  createdAt: z.string(),
});
export type RunRecordResponse = z.infer<typeof RunRecordResponseSchema>;

// ── Schema Discovery ──────────────────────────────────────────────────────────

export const ColumnInfoSchema = z.object({
  name: z.string(),
  dataType: z.string(),
  isNullable: z.boolean(),
  maxLength: z.number().nullable(),
});
export type ColumnInfo = z.infer<typeof ColumnInfoSchema>;
